import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Upload,
  Mail,
  Search,
  Filter,
  RefreshCw,
  Ship,
  Truck,
  Plane,
  Package,
  Calendar,
  User,
  MapPin,
  DollarSign,
  FileText,
  ChevronRight,
  X,
  BarChart3,
  Trash2,
} from "lucide-react";


export default function EmailArchivePage() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Filters
  const [search, setSearch] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Pagination
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 50;
  
  // Detail view
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Drag and drop
  const [isDragging, setIsDragging] = useState(false);
  
  const { getAuthHeaders } = useAuth();

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (serviceType) params.append("service_type", serviceType);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      params.append("limit", limit);
      params.append("skip", skip);
      
      const response = await axios.get(`${API_URL}/email-archive/emails?${params}`, {
        headers: getAuthHeaders(),
      });
      
      setEmails(response.data.emails || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  }, [search, serviceType, dateFrom, dateTo, skip, getAuthHeaders]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/email-archive/stats`, {
        headers: getAuthHeaders(),
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleDeleteEmail = async (e, email) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this archived email?\n\n${email.subject}`)) return;
    try {
      await axios.delete(`${API_URL}/email-archive/emails/${email.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Email deleted");
      setEmails((prev) => prev.filter((x) => x.id !== email.id));
      setTotal((t) => Math.max(0, t - 1));
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete email");
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchStats();
  }, [fetchEmails]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const msgFiles = Array.from(files).filter(f => f.name.endsWith('.msg'));
    if (msgFiles.length === 0) {
      toast.error("Please upload .msg files only");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Small batches + per-request resilience: large multipart batches can hit
      // the ingress payload/timeout limit and abort the whole upload. A few
      // files per request keeps each request small & fast, and one failed batch
      // no longer kills the rest.
      const batchSize = 3;
      let processed = 0;
      let errors = 0;
      let failedBatches = 0;

      for (let i = 0; i < msgFiles.length; i += batchSize) {
        const batch = msgFiles.slice(i, i + batchSize);
        const formData = new FormData();
        batch.forEach(file => formData.append("files", file));

        try {
          const response = await axios.post(`${API_URL}/email-archive/upload`, formData, {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "multipart/form-data",
            },
            timeout: 120000,
          });
          processed += response.data.processed || 0;
          errors += response.data.errors || 0;
        } catch (err) {
          console.error("Upload batch error:", err);
          failedBatches += 1;
          errors += batch.length;
        }
        setUploadProgress(Math.round(((i + batch.length) / msgFiles.length) * 100));
      }

      if (processed > 0) {
        toast.success(`Processed ${processed} email${processed !== 1 ? "s" : ""}${errors > 0 ? ` · ${errors} failed` : ""}`);
      } else {
        toast.error(failedBatches > 0
          ? "Upload failed — files may be too large. Try fewer/smaller .msg files."
          : "No emails could be processed from these files.");
      }
      fetchEmails();
      fetchStats();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const viewEmailDetail = async (emailId) => {
    try {
      const response = await axios.get(`${API_URL}/email-archive/emails/${emailId}`, {
        headers: getAuthHeaders(),
      });
      setSelectedEmail(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error("Failed to load email details");
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case "FCL": return <Ship className="h-4 w-4 text-blue-500" />;
      case "LCL": return <Package className="h-4 w-4 text-purple-500" />;
      case "AIR": return <Plane className="h-4 w-4 text-sky-500" />;
      case "TRUCKING": return <Truck className="h-4 w-4 text-orange-500" />;
      default: return <Mail className="h-4 w-4 text-slate-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navbar />
      <main className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Mail className="h-7 w-7 text-blue-600" />
              Email Offer Archive
            </h1>
            <p className="text-slate-600 mt-1">
              Import and search your legacy email offers
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { fetchEmails(); fetchStats(); }}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <label>
              <Button asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload .msg Files
                </span>
              </Button>
              <input
                type="file"
                multiple
                accept=".msg"
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />
            </label>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold text-slate-800">{stats.total_emails?.toLocaleString() || 0}</span>
              </div>
              <p className="text-sm text-slate-500">Total Emails</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2">
                <Ship className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold text-slate-800">{stats.by_service_type?.FCL || 0}</span>
              </div>
              <p className="text-sm text-slate-500">FCL Offers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-purple-500" />
                <span className="text-2xl font-bold text-slate-800">{stats.by_service_type?.LCL || 0}</span>
              </div>
              <p className="text-sm text-slate-500">LCL Offers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2">
                <Plane className="h-5 w-5 text-sky-500" />
                <span className="text-2xl font-bold text-slate-800">{stats.by_service_type?.AIR || 0}</span>
              </div>
              <p className="text-sm text-slate-500">Air Offers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-500" />
                <span className="text-2xl font-bold text-slate-800">{stats.top_customers?.length || 0}</span>
              </div>
              <p className="text-sm text-slate-500">Unique Customers</p>
            </div>
          </div>
        )}

        {/* Drag & Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-slate-300 bg-white hover:border-slate-400"
          }`}
        >
          {uploading ? (
            <div className="space-y-3">
              <RefreshCw className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
              <p className="text-slate-600">Processing emails... {uploadProgress}%</p>
              <div className="w-64 mx-auto bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="text-slate-600 font-medium">
                Drag & drop .msg files here
              </p>
              <p className="text-sm text-slate-500">
                or click "Upload .msg Files" button above
              </p>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-slate-500 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Customer, route, subject..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="text-sm text-slate-500 mb-1 block">Service Type</label>
              <Select value={serviceType || "all"} onValueChange={(v) => setServiceType(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="FCL">FCL</SelectItem>
                  <SelectItem value="LCL">LCL</SelectItem>
                  <SelectItem value="AIR">Air</SelectItem>
                  <SelectItem value="TRUCKING">Trucking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <label className="text-sm text-slate-500 mb-1 block">Date From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="w-40">
              <label className="text-sm text-slate-500 mb-1 block">Date To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setSearch("");
                setServiceType("");
                setDateFrom("");
                setDateTo("");
                setSkip(0);
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>

        {/* Email List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex justify-between items-center">
            <h2 className="font-semibold text-slate-800">
              Archived Offers ({total.toLocaleString()})
            </h2>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>Page {Math.floor(skip / limit) + 1} of {Math.ceil(total / limit)}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + limit >= total}
                onClick={() => setSkip(skip + limit)}
              >
                Next
              </Button>
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
              <p className="text-slate-500 mt-2">Loading emails...</p>
            </div>
          ) : emails.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No archived emails yet</p>
              <p className="text-sm text-slate-400">Upload .msg files to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => viewEmailDetail(email.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getServiceIcon(email.service_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900 truncate">
                          {email.subject}
                        </span>
                        {email.service_type && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            email.service_type === "FCL" ? "bg-blue-100 text-blue-700" :
                            email.service_type === "LCL" ? "bg-purple-100 text-purple-700" :
                            email.service_type === "AIR" ? "bg-sky-100 text-sky-700" :
                            "bg-orange-100 text-orange-700"
                          }`}>
                            {email.service_type}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                        {email.customer_company && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {email.customer_company}
                            {email.customer_contact && ` (${email.customer_contact})`}
                          </span>
                        )}
                        {email.incoterm && (
                          <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                            {email.incoterm}
                          </span>
                        )}
                        {(email.origin || email.destination) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {email.origin || "?"} → {email.destination || "?"}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(email.date)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => handleDeleteEmail(e, email)}
                        title="Delete email"
                        data-testid={`email-archive-delete-${email.id}`}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronRight className="h-5 w-5 text-slate-300" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email Detail Modal */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedEmail && getServiceIcon(selectedEmail.service_type)}
                Offer Details
                {selectedEmail?.ai_processed && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    AI Processed
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedEmail && (
              <div className="space-y-6">
                {/* Header Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-slate-900 mb-2">{selectedEmail.subject}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">From:</span> {selectedEmail.sender}</div>
                    <div><span className="text-slate-500">Date:</span> {formatDate(selectedEmail.date)}</div>
                  </div>
                </div>
                
                {/* Customer Info */}
                {selectedEmail.offer?.customer && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Customer Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {selectedEmail.offer.customer.company_name && (
                        <div>
                          <p className="text-xs text-blue-600">Company</p>
                          <p className="font-medium text-blue-900">{selectedEmail.offer.customer.company_name}</p>
                        </div>
                      )}
                      {selectedEmail.offer.customer.contact_person && (
                        <div>
                          <p className="text-xs text-blue-600">Contact Person</p>
                          <p className="font-medium text-blue-900">{selectedEmail.offer.customer.contact_person}</p>
                        </div>
                      )}
                      {selectedEmail.offer.customer.email && (
                        <div>
                          <p className="text-xs text-blue-600">Email</p>
                          <p className="font-medium text-blue-900">{selectedEmail.offer.customer.email}</p>
                        </div>
                      )}
                      {selectedEmail.offer.customer.country && (
                        <div>
                          <p className="text-xs text-blue-600">Country</p>
                          <p className="font-medium text-blue-900">{selectedEmail.offer.customer.country}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Service & Routing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Service Info */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-3">Service Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedEmail.service_type && (
                        <div>
                          <p className="text-xs text-slate-500">Service Type</p>
                          <p className="font-medium">{selectedEmail.service_type}</p>
                        </div>
                      )}
                      {selectedEmail.offer?.incoterm && (
                        <div>
                          <p className="text-xs text-slate-500">Incoterm</p>
                          <p className="font-medium">{selectedEmail.offer.incoterm}</p>
                        </div>
                      )}
                      {selectedEmail.offer?.equipment && (
                        <div>
                          <p className="text-xs text-slate-500">Equipment</p>
                          <p className="font-medium">{selectedEmail.offer.equipment}</p>
                        </div>
                      )}
                      {selectedEmail.offer?.shipping_line && (
                        <div>
                          <p className="text-xs text-slate-500">Shipping Line</p>
                          <p className="font-medium">{selectedEmail.offer.shipping_line}</p>
                        </div>
                      )}
                      {selectedEmail.offer?.transit_time_days && (
                        <div>
                          <p className="text-xs text-slate-500">Transit Time</p>
                          <p className="font-medium">{selectedEmail.offer.transit_time_days} days</p>
                        </div>
                      )}
                      {selectedEmail.offer?.valid_until && (
                        <div>
                          <p className="text-xs text-slate-500">Valid Until</p>
                          <p className="font-medium text-red-600">{selectedEmail.offer.valid_until}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Route Info */}
                  {selectedEmail.offer?.route && (
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Routing
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-emerald-600 w-20">Origin:</span>
                          <span className="font-medium">
                            {[
                              selectedEmail.offer.route.origin_city,
                              selectedEmail.offer.route.origin_country,
                              selectedEmail.offer.route.origin_port && `(${selectedEmail.offer.route.origin_port})`
                            ].filter(Boolean).join(", ")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-emerald-600 w-20">Destination:</span>
                          <span className="font-medium">
                            {[
                              selectedEmail.offer.route.destination_city,
                              selectedEmail.offer.route.destination_country,
                              selectedEmail.offer.route.destination_port && `(${selectedEmail.offer.route.destination_port})`
                            ].filter(Boolean).join(", ")}
                          </span>
                        </div>
                        {selectedEmail.offer.route.final_destination && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-600 w-20">Final Dest:</span>
                            <span className="font-medium">{selectedEmail.offer.route.final_destination}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Cargo Info */}
                {selectedEmail.offer?.cargo && Object.values(selectedEmail.offer.cargo).some(v => v) && (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Cargo Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {selectedEmail.offer.cargo.description && (
                        <div className="col-span-2">
                          <p className="text-xs text-amber-600">Description</p>
                          <p className="font-medium">{selectedEmail.offer.cargo.description}</p>
                        </div>
                      )}
                      {selectedEmail.offer.cargo.hs_codes?.length > 0 && (
                        <div>
                          <p className="text-xs text-amber-600">HS Codes</p>
                          <p className="font-medium">{selectedEmail.offer.cargo.hs_codes.join(", ")}</p>
                        </div>
                      )}
                      {selectedEmail.offer.cargo.weight_kg && (
                        <div>
                          <p className="text-xs text-amber-600">Weight</p>
                          <p className="font-medium">{selectedEmail.offer.cargo.weight_kg} kg</p>
                        </div>
                      )}
                      {selectedEmail.offer.cargo.volume_cbm && (
                        <div>
                          <p className="text-xs text-amber-600">Volume</p>
                          <p className="font-medium">{selectedEmail.offer.cargo.volume_cbm} CBM</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Pricing Breakdown */}
                {selectedEmail.offer?.pricing && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Pricing Breakdown
                    </h4>
                    <div className="space-y-3">
                      {/* Main Rates */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedEmail.offer.pricing.ocean_freight && (
                          <RateCard 
                            label="Ocean Freight" 
                            rate={selectedEmail.offer.pricing.ocean_freight} 
                            color="blue"
                          />
                        )}
                        {selectedEmail.offer.pricing.trucking_origin && (
                          <RateCard 
                            label="Trucking (Origin)" 
                            rate={selectedEmail.offer.pricing.trucking_origin} 
                            color="orange"
                          />
                        )}
                        {selectedEmail.offer.pricing.trucking_destination && (
                          <RateCard 
                            label="Trucking (Dest)" 
                            rate={selectedEmail.offer.pricing.trucking_destination} 
                            color="orange"
                          />
                        )}
                        {selectedEmail.offer.pricing.thc_origin && (
                          <RateCard 
                            label="THC Origin" 
                            rate={selectedEmail.offer.pricing.thc_origin} 
                            color="slate"
                          />
                        )}
                        {selectedEmail.offer.pricing.thc_destination && (
                          <RateCard 
                            label="THC Destination" 
                            rate={selectedEmail.offer.pricing.thc_destination} 
                            color="slate"
                          />
                        )}
                        {selectedEmail.offer.pricing.export_customs && (
                          <RateCard 
                            label="Export Customs" 
                            rate={selectedEmail.offer.pricing.export_customs} 
                            color="purple"
                          />
                        )}
                        {selectedEmail.offer.pricing.import_customs && (
                          <RateCard 
                            label="Import Customs" 
                            rate={selectedEmail.offer.pricing.import_customs} 
                            color="purple"
                          />
                        )}
                        {selectedEmail.offer.pricing.bl_fee && (
                          <RateCard 
                            label="B/L Fee" 
                            rate={selectedEmail.offer.pricing.bl_fee} 
                            color="slate"
                          />
                        )}
                      </div>
                      
                      {/* Other charges */}
                      {selectedEmail.offer.pricing.other_charges?.length > 0 && (
                        <div>
                          <p className="text-xs text-green-600 mb-2">Other Charges</p>
                          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                            {selectedEmail.offer.pricing.other_charges.map((charge, idx) => (
                              <div key={idx} className="bg-white rounded p-2 text-xs">
                                <p className="text-slate-500">{charge.service_name}</p>
                                <p className="font-medium">
                                  {charge.amount} {charge.currency} {charge.unit && `/ ${charge.unit}`}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Raw sections fallback */}
                      {selectedEmail.offer.pricing.raw_sections && Object.keys(selectedEmail.offer.pricing.raw_sections).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <p className="text-xs text-green-600 mb-2">Raw Pricing Data</p>
                          <div className="space-y-2">
                            {Object.entries(selectedEmail.offer.pricing.raw_sections).map(([key, value]) => (
                              <div key={key} className="bg-white rounded p-2">
                                <p className="text-xs font-medium text-slate-600 capitalize">{key.replace(/_/g, ' ')}</p>
                                <pre className="text-xs text-slate-500 whitespace-pre-wrap">{value}</pre>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Raw Email Body */}
                {selectedEmail.body_text && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Original Email</h4>
                    <pre className="bg-slate-50 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedEmail.body_text}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

// Rate Card Component
function RateCard({ label, rate, color }) {
  if (!rate || (!rate.amount && !rate.raw_text)) return null;
  
  const colorClasses = {
    blue: "bg-blue-100 text-blue-800",
    orange: "bg-orange-100 text-orange-800",
    purple: "bg-purple-100 text-purple-800",
    slate: "bg-slate-100 text-slate-800",
    green: "bg-green-100 text-green-800",
  };
  
  return (
    <div className={`rounded p-2 ${colorClasses[color] || colorClasses.slate}`}>
      <p className="text-xs opacity-75">{label}</p>
      <p className="font-semibold">
        {rate.amount ? `${rate.amount} ${rate.currency || ''}` : rate.raw_text}
      </p>
      {rate.unit && <p className="text-xs opacity-75">{rate.unit}</p>}
      {rate.notes && <p className="text-xs opacity-75">{rate.notes}</p>}
    </div>
  );
}

export { EmailArchivePage };
